/**
 * The technician's own account reads and writes (FM Technician rebuild R2,
 * slice "Me": B16, B17, B24). Every route is maintenance:work
 * (backend/routers/tech_me.py); callers pass `enabled` as their gate so no
 * other role ever calls one.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut } from '@/utils/api';

export interface AlertType {
  event: string;
  label: string;
  /** Buzzes the phone (push). Off = in the inbox only. */
  on: boolean;
  always_on: boolean;
  class: 'urgent' | 'info' | 'p1';
}
export interface AlertSettings {
  mute_outside_shift: boolean;
  shift: { id: string; name: string; site_name: string; start: string; end: string } | null;
  p1_sound: boolean;
  types: AlertType[];
  classes: Record<string, 'urgent' | 'info' | 'p1'>;
}
export interface AlertSettingsUpdate {
  mute_outside_shift?: boolean;
  p1_sound?: boolean;
  types?: Record<string, boolean>;
}

export const ALERT_SETTINGS_KEY = ['tech', 'alert-settings'] as const;
export const TECH_PROFILE_KEY = ['tech', 'profile'] as const;

export function useAlertSettings(enabled: boolean) {
  return useQuery<AlertSettings>({
    queryKey: ALERT_SETTINGS_KEY,
    queryFn: () => apiGet<AlertSettings>('/tech/alert-settings'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSaveAlertSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AlertSettingsUpdate) => apiPut<AlertSettings>('/tech/alert-settings', body),
    onSuccess: (data) => {
      qc.setQueryData(ALERT_SETTINGS_KEY, data);
      void qc.invalidateQueries({ queryKey: TECH_PROFILE_KEY });
    },
  });
}

export interface DesignatedClass {
  id: string; class: 'LT' | 'HT' | 'WIREMAN'; certificate_no: string; valid_until: string; expired: boolean;
}
export interface TechProfile {
  id: string;
  name: string;
  email: string;
  trade: string | null;
  site_mode: 'all' | 'some' | 'none';
  sites: { id: string; name: string; timezone: string | null }[];
  shift: AlertSettings['shift'];
  alerts: { mute_outside_shift: boolean; p1_sound: boolean };
  designated: DesignatedClass[];
  pin_set: boolean;
  pin_set_at: string | null;
  consent: {
    published: boolean;
    current_version: number | null;
    given_at: string | null;
    notice_version: number | null;
    up_to_date: boolean;
  };
  onboarded_at: string | null;
}

export function useTechProfile(enabled: boolean) {
  return useQuery<TechProfile>({
    queryKey: TECH_PROFILE_KEY,
    queryFn: () => apiGet<TechProfile>('/tech/profile'),
    enabled,
    staleTime: 60_000,
  });
}

export interface PinBody { pin: string; current_pin?: string; password?: string }

export function useSetPin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PinBody) => apiPost<{ pin_set: boolean; pin_set_at: string }>('/tech/pin', body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: TECH_PROFILE_KEY }); },
  });
}

export function useGiveConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ consent_id: string; notice_version: number; given_at: string }>('/tech/consent'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TECH_PROFILE_KEY });
      void qc.invalidateQueries({ queryKey: ['privacy'] });
    },
  });
}

export function useTestAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ sent: boolean }>('/tech/test-alert'),
    // The test lands in the inbox too: the bell and Alerts refresh.
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['notifications'] }); },
  });
}

export function useFinishFirstRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ onboarded_at: string }>('/tech/onboarded'),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: TECH_PROFILE_KEY }); },
  });
}
